import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, isPageVisible, canEdit } from '../context/AuthContext';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { 
  Home, 
  FileText, 
  Settings, 
  LogOut, 
  PlusCircle,
  CheckCircle2,
  Clock,
  FlaskConical,
  Flame,
  Package,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ClipboardCheck,
  Factory,
  Search,
  Box,
  Users,
  Shield,
  Server,
  ChefHat,
  ArrowLeftRight
} from 'lucide-react';
import { Button } from './ui/button';

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sensorikaOpen, setSensorikaOpen] = useState(
    location.pathname.includes('/senzorika')
  );
  const [adminOpen, setAdminOpen] = useState(
    location.pathname.includes('/admin')
  );

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Keep submenu open when on senzorika pages
  useEffect(() => {
    if (location.pathname.includes('/senzorika')) {
      setSensorikaOpen(true);
    }
    if (location.pathname.includes('/admin')) {
      setAdminOpen(true);
    }
  }, [location.pathname]);

  // Build navigation based on user permissions
  const buildNavigation = () => {
    const nav = [];
    
    // Domů (Home) - always visible
    nav.push({ name: 'Domů', path: '/', icon: Home });
    
    // Hlášení
    if (isPageVisible(user, 'reports')) {
      nav.push({ name: 'Hlášení', path: '/reports', icon: FileText, pageKey: 'reports' });
    }
    
    // Senzorika submenu
    const showHotovky = isPageVisible(user, 'sensory_hotovky');
    const showKrabicky = isPageVisible(user, 'sensory_krabicky');
    
    if (showHotovky || showKrabicky) {
      const submenu = [];
      if (showHotovky) submenu.push({ name: 'Hotovky', path: '/senzorika/hotovky', pageKey: 'sensory_hotovky' });
      if (showKrabicky) submenu.push({ name: 'Krabičky', path: '/senzorika/krabicky', pageKey: 'sensory_krabicky' });
      // Analytics available if either type is visible
      submenu.push({ name: 'Hodnocení', path: '/senzorika/analytika', pageKey: 'sensory_hotovky' });
      
      nav.push({ 
        name: 'Senzorika', 
        icon: FlaskConical,
        submenu
      });
    }
    
    // Autokláv
    if (isPageVisible(user, 'autoclave')) {
      nav.push({ name: 'Autokláv', path: '/autoklav', icon: Flame, pageKey: 'autoclave' });
    }
    
    // Detail šarže
    if (isPageVisible(user, 'batch_search')) {
      nav.push({ name: 'Detail šarže', path: '/sarze', icon: Search, pageKey: 'batch_search' });
    }
    
    // Úkoly - přístupné všem
    nav.push({ name: 'Úkoly', path: '/ukoly', icon: ClipboardList, pageKey: 'tasks' });
    
    // Interní inspekce - přístupné všem
    nav.push({ name: 'Interní inspekce', path: '/audity', icon: ClipboardCheck, pageKey: 'audits' });
    
    // Výroba
    if (isPageVisible(user, 'production')) {
      nav.push({ name: 'Výroba', path: '/vyroba', icon: Factory, pageKey: 'production' });
    }
    
    // Kuchyně
    if (isPageVisible(user, 'kitchen')) {
      nav.push({ name: 'Kuchyně', path: '/kuchyne', icon: ChefHat, pageKey: 'kitchen' });
    }
    
    // Balírna
    if (isPageVisible(user, 'balirna')) {
      nav.push({ name: 'Balírna', path: '/balirna', icon: Package, pageKey: 'balirna' });
    }
    
    // Záznamy předávky
    if (isPageVisible(user, 'kitchen') || isPageVisible(user, 'balirna')) {
      nav.push({ name: 'Záznamy předávky', path: '/zaznamy-predavky', icon: ArrowLeftRight, pageKey: 'kitchen' });
    }
    
    // Administrace - simple link to admin page with tiles
    if (isPageVisible(user, 'admin')) {
      nav.push({ name: 'Administrace', path: '/admin', icon: Settings, pageKey: 'admin' });
    }
    
    return nav;
  };

  const navigation = buildNavigation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if user can create new reports
  const canCreateReport = canEdit(user, 'reports');

  return (
    <div className="flex h-screen bg-[#f0f9ff]">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-cyan-200 flex flex-col shadow-sm">
        <div className="p-6 border-b border-cyan-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-cyan-900 leading-tight">Kvalita Zdravé</h1>
              <h1 className="text-lg font-bold text-emerald-700 leading-tight">Stravování</h1>
            </div>
          </div>
          {/* Current Date and Time */}
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
            <Clock className="h-4 w-4 text-cyan-500" />
            <div>
              <div className="font-medium text-slate-700">
                {format(currentTime, 'EEEE', { locale: cs })}
              </div>
              <div className="flex items-center gap-2">
                <span>{format(currentTime, 'dd.MM.yyyy', { locale: cs })}</span>
                <span className="font-mono font-semibold text-cyan-600">
                  {format(currentTime, 'HH:mm:ss')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            
            // Handle submenu items
            if (item.submenu) {
              const isAnySubActive = item.submenu.some(sub => 
                location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
              );
              
              // Determine which state to use based on pageKey
              const isOpen = item.pageKey === 'admin' ? adminOpen : sensorikaOpen;
              const setOpen = item.pageKey === 'admin' ? setAdminOpen : setSensorikaOpen;
              
              // Different colors for different menus
              const activeGradient = item.pageKey === 'admin' 
                ? 'bg-gradient-to-r from-slate-600 to-slate-700 text-white shadow-md'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md';
              const activeSubBg = item.pageKey === 'admin'
                ? 'bg-slate-100 text-slate-700 font-medium'
                : 'bg-purple-100 text-purple-700 font-medium';
              
              return (
                <div key={item.name}>
                  <button
                    onClick={() => setOpen(!isOpen)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isAnySubActive
                        ? activeGradient
                        : 'text-cyan-700 hover:bg-cyan-50 hover:text-cyan-900'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.name}</span>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {isOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.submenu.map((sub) => {
                        const SubIcon = sub.icon;
                        const isSubActive = location.pathname === sub.path || location.pathname.startsWith(sub.path + '/');
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            data-testid={`nav-${sub.path.replace(/\//g, '-').substring(1)}`}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                              isSubActive
                                ? activeSubBg
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            {SubIcon ? <SubIcon className="h-4 w-4" /> : <div className="w-2 h-2 rounded-full bg-current opacity-50" />}
                            <span>{sub.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Regular menu items
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.substring(1)}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-white shadow-md'
                    : 'text-cyan-700 hover:bg-cyan-50 hover:text-cyan-900'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-cyan-100">
          {canCreateReport && (
            <div className="mb-3">
              <Button
                onClick={() => navigate('/reports/new')}
                data-testid="new-report-button"
                className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white shadow-md"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Nové hlášení
              </Button>
            </div>
          )}
          <div className="flex items-center justify-between px-3 py-2 bg-cyan-50 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white flex items-center justify-center text-sm font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-cyan-900">{user?.name}</p>
                <p className="text-xs text-cyan-600">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-cyan-400 hover:text-cyan-600 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
};